import { CommonUtility } from '@utility/common';
import { createRef, useCallback, useEffect } from 'react';
import ReactTextMask from 'react-text-mask';
import { createNumberMask } from 'text-mask-addons';
import { cn } from '@skupreme/shared/ui';
import { domProps } from './form-field/chrome';
import { FIELD_INPUT } from './form-field/styles';

const MaskedInput =
  typeof ReactTextMask === 'function' ? ReactTextMask : ReactTextMask.default;

const defaultMaskOptions = {
  prefix: '',
  allowDecimal: false,
  allowNegative: false,
  allowLeadingZeroes: true,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
};

export const defaultCurrencyMaskOptions = {
  ...defaultMaskOptions,
  prefix: '$',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
  allowDecimal: true,
  decimalSymbol: '.',
  decimalLimit: 4,
  integerLimit: 6,
};

/*
 * The masked inputs take the kit's input look. Callers spread their own
 * props (`maxValue`, Semantic's `fluid`…) through; only valid DOM ones reach
 * the `<input>`, as the old styled wrappers filtered them.
 */
function maskedInputProps(
  inputProps: Record<string, unknown>,
  ...classes: (string | false | undefined)[]
) {
  const { className, ...rest } = inputProps;
  return {
    ...domProps(rest),
    className: cn(
      FIELD_INPUT,
      ...classes,
      className as string | undefined,
      'sk:m-0!',
    ),
  };
}

interface CurrencyMaskInputProps {
  currencyCode?: string;
  maskOptions?: Partial<typeof defaultCurrencyMaskOptions>;
  value?: string | number;
  onChange: (value: string) => void;
  isGroupPrefix?: boolean;
  [key: string]: any;
}

export function CurrencyMaskInput({
  currencyCode,
  maskOptions = {},
  value,
  onChange,
  isGroupPrefix = false,
  ...inputProps
}: CurrencyMaskInputProps) {
  const symbol = CommonUtility.currencySymbol(currencyCode);
  const prefix = currencyCode ? symbol : '$';
  const options = {
    ...defaultCurrencyMaskOptions,
    ...maskOptions,
    prefix: isGroupPrefix ? '' : prefix,
  };
  const currencyMask = createNumberMask(options);

  const field = (
    <MaskedInput
      mask={currencyMask}
      value={value}
      onChange={e =>
        onChange(
          options?.allowNegative === true
            ? e.target.value
            : e.target.value.replace(/[^0-9.]+/g, ''),
        )
      }
      {...maskedInputProps(inputProps, isGroupPrefix && 'sk:pl-7!')}
    />
  );

  if (!isGroupPrefix) {
    return field;
  }

  return (
    <div className="sk:relative sk:flex sk:w-full sk:items-center">
      <span className="sk:pointer-events-none sk:absolute sk:left-3 sk:text-dense sk:text-fg-tertiary">
        {prefix}
      </span>
      {field}
    </div>
  );
}

interface NumberMaskInputProps {
  maskOptions?: Partial<typeof defaultMaskOptions>;
  minValue?: number;
  maxValue?: number;
  value?: string | number | null;
  onChange: (value: string) => void;
  inputLeft?: boolean;
  [key: string]: any;
}

export function NumberMaskInput({
  maskOptions,
  minValue,
  maxValue,
  value: passedValue,
  onChange,
  inputLeft,
  ...inputProps
}: NumberMaskInputProps) {
  const value = ['number', 'string'].includes(typeof passedValue)
    ? passedValue
    : '';

  const ref = createRef<{ initTextMask?: () => void }>();
  const numberMask = createNumberMask({
    ...defaultMaskOptions,
    ...(maskOptions || {}),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-applies the mask when the bounds change, as before
  useEffect(() => {
    ref.current?.initTextMask?.();
  }, [maxValue, minValue]);

  const onPipe = useCallback(
    confirmedValue => {
      const numberToTest = confirmedValue.replace(/,/g, '');
      if (minValue === undefined && maxValue === undefined) {
        return confirmedValue;
      }

      if (
        minValue !== undefined &&
        maxValue !== undefined &&
        !Number.isNaN(Number(numberToTest)) &&
        Number(numberToTest) >= minValue &&
        Number(numberToTest) <= maxValue
      ) {
        return confirmedValue;
      }

      if (
        minValue !== undefined &&
        maxValue === undefined &&
        !Number.isNaN(Number(numberToTest)) &&
        Number(numberToTest) >= minValue
      ) {
        return confirmedValue;
      }

      if (
        maxValue !== undefined &&
        minValue === undefined &&
        !Number.isNaN(Number(numberToTest)) &&
        Number(numberToTest) <= maxValue
      ) {
        return confirmedValue;
      }

      if ([' ', '-', '_'].includes(confirmedValue)) {
        return confirmedValue;
      }
      return false;
    },
    [maxValue, minValue],
  );

  return (
    <MaskedInput
      ref={ref}
      mask={numberMask}
      value={value}
      onChange={e => onChange(e.target.value.replace(/[^0-9.]+/g, ''))}
      pipe={onPipe}
      {...maskedInputProps(inputProps, inputLeft && 'sk:pl-5!')}
    />
  );
}

export function PercentageMaskInput({
  maskOptions,
  value,
  onChange,
  ...inputProps
}) {
  const options = {
    ...defaultMaskOptions,
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 2,
    integerLimit: 3,
    ...(maskOptions || {}),
  };
  const numberMask = createNumberMask(options);
  return (
    <MaskedInput
      mask={numberMask}
      value={value}
      onChange={e =>
        onChange(
          options?.allowNegative === true
            ? e.target.value
            : e.target.value.replace(/[^0-9.]+/g, ''),
        )
      }
      pipe={confirmedValue => {
        if (
          !Number.isNaN(Number(confirmedValue)) &&
          Number(confirmedValue) <= 100 &&
          Number(confirmedValue) >= -100
        ) {
          return confirmedValue;
        }
        if (
          confirmedValue === ' ' ||
          confirmedValue === '-' ||
          confirmedValue === '_'
        ) {
          return confirmedValue;
        }
        return false;
      }}
      {...maskedInputProps(inputProps)}
    />
  );
}

export function MaskInput({ mask, value, onChange, ...inputProps }) {
  return (
    <MaskedInput
      mask={mask}
      value={value}
      guide={false}
      onChange={e => {
        onChange(e.target.value.replace(/[^0-9]+/g, ''));
      }}
      {...maskedInputProps(inputProps)}
    />
  );
}
